import { CommentOutlined } from "@ant-design/icons";
import {
  Col,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Tooltip,
  Typography,
} from "antd";
import type { CatalogPriceZoneSPPriceFamily } from "hooks/catalog-price-zone-service-provider";
import type { PriceFamily, PriceType } from "models";
import { Fragment, JSX } from "react";
import { PRICE_TYPE_LOGICS, PRICE_TYPES } from "utils/constant";
import type { OrderPrestataireLine } from ".";
const { Text } = Typography;

interface CatalogPriceLineProductsCellProps {
  form: FormInstance;
  value?: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    isManualPrice?: boolean;
    wasInitiallyManual?: boolean;
    catalogPriceValue?: string | number;
    comment?: string | null;
    quantity?: number;
  }[];
  record: OrderPrestataireLine;
  priceTypes: PriceType[];
  onSelectPriceFamily: (args: {
    uuid?: string;
    productId?: number;
    priceFamilyId: number;
  }) => void;
  selectedProductsFromOrderClients: {
    priceFamilyId?: number;
    productId?: number;
    quantity?: number;
    productName?: string;
    documentProductLineId?: number;
  }[];
  updateDataSource?: (
    data: OrderPrestataireLine,
    serviceProviderId?: number
  ) => void;
}

const CatalogPriceLineProductsCell = (
  props: CatalogPriceLineProductsCellProps
) => {
  const {
    form,
    value,
    record,
    priceTypes,
    onSelectPriceFamily,
    updateDataSource,
  } = props;
  return (
    <div>
      {value?.map((item, index) => {
        const hasTarifSection =
          item?.priceFamilies && item.priceFamilies.length > 0;
        return (
          <div key={item?.uuid + "-" + item?.productId}>
            <div key={index}>
              <Row
                gutter={[16, 0]}
                align="middle"
                className="generate-price-value-cell"
              >
                <Col
                  xs={{ span: 24 }}
                  className="text-align-center"
                  style={{ height: "32px" }}
                >
                  <div className="text-tarif text-tarif-wrapper">
                    <Text strong className="mr-2">
                      {item?.productName}
                    </Text>
                    <Tooltip
                      placement="bottom"
                      title={item?.comment || ""}
                      overlayInnerStyle={{
                        whiteSpace: "pre-line",
                        background: "#A6C84D",
                        color: "rgba(0, 0, 0, 0.88)",
                        fontWeight: 500,
                      }}
                    >
                      <CommentOutlined />
                    </Tooltip>
                  </div>
                  <Form.Item
                    className="hidden"
                    name={[
                      "lineItems",
                      `${record?.serviceProviderId}`,
                      "products",
                      `${item?.uuid}-${item?.productId}`,
                      "productId",
                    ]}
                    initialValue={item?.productId}
                  ></Form.Item>
                  <Form.Item
                    className="hidden"
                    name={[
                      "lineItems",
                      `${record?.serviceProviderId}`,
                      "products",
                      `${item?.uuid}-${item?.productId}`,
                      "quantity",
                    ]}
                    initialValue={item?.quantity ?? 1}
                  >
                    <InputNumber />
                  </Form.Item>
                </Col>
              </Row>
              {hasTarifSection && (
                <div className="generate-price-value-cell">
                  {GenerateTarifSection(
                    form,
                    record,
                    item?.priceFamilies || [],
                    priceTypes,
                    onSelectPriceFamily,
                    item?.uuid,
                    item?.productId,
                    item?.selectedPriceFamilyId,
                    updateDataSource
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const GenerateTarifSection = (
  form: FormInstance,
  record: OrderPrestataireLine,
  priceFamilies: CatalogPriceZoneSPPriceFamily[],
  priceTypes: PriceType[],
  onSelectPriceFamily: (args: {
    uuid?: string;
    productId?: number;
    priceFamilyId: number;
  }) => void,
  uuid?: string,
  productId?: number,
  priceFamilyId?: number | null,
  updateDataSource?: (
    data: OrderPrestataireLine,
    serviceProviderId?: number
  ) => void
) => {
  let content: JSX.Element = <></>;
  const selectedPriceFamily = priceFamilies.find(
    (priceFamily) => priceFamily.id === priceFamilyId
  );
  const selectionPriceType = priceTypes?.find(
    (priceType) => priceType.key === PRICE_TYPES.selection
  );
  const numberPriceType = priceTypes?.find(
    (priceType) => priceType.key === PRICE_TYPES.numberInput
  );

  // Sync the price in price cell and state of OrderPrestataireLine
  const handlePriceValueSync = (
    priceFamilyId?: number,
    priceId?: number,
    priceOptionId?: number
  ) => {
    if (!updateDataSource) return;

    const priceValueFromForm = form.getFieldValue([
      "lineItems",
      `${record.serviceProviderId}`,
      "products",
      `${uuid}-${productId}`,
      "prixUnitaire",
    ]);
    const selectedProduct = record.products.find(
      (p) => p.uuid === uuid && p.productId === productId
    );

    if (!selectedProduct) return;

    const updatedProduct = {
      ...selectedProduct,
      priceFamilies: [...(selectedProduct.priceFamilies || [])],
    };

    const calculateIsManual = (currentValue: string | number, catalogValue?: string | number, wasManual?: boolean) => {
      if (wasManual) return true;
      if (catalogValue !== undefined && catalogValue !== null && catalogValue !== "") {
        return Number(currentValue) !== Number(catalogValue);
      }
      return (
        currentValue !== "" &&
        currentValue !== "0" &&
        currentValue !== null &&
        Number(currentValue) !== 0
      );
    };

    if (priceOptionId && priceFamilyId && priceId) {
      const priceOptionValueForm = form.getFieldValue([
        "lineItems",
        `${record.serviceProviderId}`,
        "products",
        `${uuid}-${productId}`,
        "tarifItems",
        "productLinePrices",
        "priceOption",
        priceOptionId,
        "priceOptionValue",
      ]);
      updatedProduct.priceFamilies = updatedProduct.priceFamilies.map((pf) =>
        pf.id === priceFamilyId
          ? {
            ...pf,
            prices: (pf.prices || []).map((price) =>
              price.id === priceId
                ? {
                  ...price,
                  priceOptions: (price.priceOptions || []).map((option) =>
                    option.id === priceOptionId
                      ? {
                        ...option,
                        priceValue: priceOptionValueForm,
                        isManualPrice: calculateIsManual(
                          priceOptionValueForm,
                          option.catalogPriceValue,
                          option.wasInitiallyManual,
                        ),
                      }
                      : option
                  ),
                }
                : price
            ),
          }
          : pf
      );
    } else if (priceId && priceFamilyId) {
      const priceValueForm = form.getFieldValue([
        "lineItems",
        `${record.serviceProviderId}`,
        "products",
        `${uuid}-${productId}`,
        "tarifItems",
        "productLinePrices",
        priceId,
        "priceValue",
      ]);
      updatedProduct.priceFamilies = updatedProduct.priceFamilies.map((pf) =>
        pf.id === priceFamilyId
          ? {
            ...pf,
            prices: (pf.prices || []).map((price) =>
              price.id === priceId
                ? {
                  ...price,
                  priceValue: priceValueForm,
                  isManualPrice: calculateIsManual(
                    priceValueForm,
                    price.catalogPriceValue,
                    price.wasInitiallyManual,
                  ),
                }
                : price
            ),
          }
          : pf
      );
    } else if (priceFamilyId) {
      const priceFamilyValueFrom = form.getFieldValue([
        "lineItems",
        `${record.serviceProviderId}`,
        "products",
        `${uuid}-${productId}`,
        "tarifItems",
        `${priceFamilyId}`,
        "priceFamilyValue",
      ]);
      updatedProduct.priceFamilies = updatedProduct.priceFamilies.map((pf) =>
        pf.id === priceFamilyId
          ? {
            ...pf,
            priceValue: priceFamilyValueFrom,
            isManualPrice: calculateIsManual(
              priceFamilyValueFrom,
              pf.catalogPriceValue,
              pf.wasInitiallyManual,
            ),
          }
          : pf
      );
    }

    updatedProduct.priceValue =
      priceValueFromForm !== undefined && priceValueFromForm !== null
        ? priceValueFromForm.toString()
        : "";

    if (updatedProduct.priceFamilies && updatedProduct.priceFamilies.length > 0) {
      updatedProduct.isManualPrice = updatedProduct.priceFamilies.some(pf => {
        return pf.id === priceFamilyId && pf.isManualPrice;
      });
    } else {
      updatedProduct.isManualPrice = calculateIsManual(
        updatedProduct.priceValue,
        updatedProduct.catalogPriceValue,
        updatedProduct.wasInitiallyManual
      );
    }

    updateDataSource(
      {
        ...record,
        products: record.products.map((p) =>
          p.uuid === uuid && p.productId === productId
            ? {
              ...p,
              priceFamilies: updatedProduct.priceFamilies,
              priceValue: updatedProduct.priceValue,
              isManualPrice: updatedProduct.isManualPrice,
            }
            : p
        ),
      },
      record.serviceProviderId
    );
  };

  if (priceFamilies.length > 0) {
    content = (
      <>
        <Row gutter={[16, 0]} className="generate-tarif-section__algin">
          <Col xs={{ span: 24 }} className="price-option">
            <Form.Item
              label="Type du tarif"
              labelCol={{ span: 17 }}
              className="long-label-item-tarif"
              name={[
                "lineItems",
                `${record?.serviceProviderId}`,
                "products",
                `${uuid + "-" + productId}`,
                "tarifItems",
                "priceFamilyId",
              ]}
              rules={[{ required: true }]}
              initialValue={selectedPriceFamily?.id}
            >
              <Select
                className="type-du-tarif-selection"
                placeholder="Sélectionner"
                showSearch={false}
                disabled={true}
                options={priceFamilies.map((priceFamily) => ({
                  value: priceFamily.id,
                  label: priceFamily.name,
                }))}
                onSelect={(value) =>
                  onSelectPriceFamily({
                    uuid: uuid,
                    productId: productId,
                    priceFamilyId: value,
                  })
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {selectedPriceFamily && (
          <Row gutter={[16, 0]} className="generate-tarif-section__algin">
            <Col className="price-option">
              <Form.Item
                label={selectedPriceFamily?.labelName ?? ""}
                labelCol={{ span: 17 }}
                className="long-label-item"
              >
                <Tooltip
                  title={(() => {
                    const familyPrice = selectedPriceFamily?.priceValue;
                    if (!familyPrice || Number(familyPrice) === 0) {
                      return "Pas de prix";
                    }
                    const isManual = selectedPriceFamily?.isManualPrice;

                    return isManual ? "Prix manuel" : "Prix catalogue";
                  })()}
                  placement="right"
                  align={{ offset: [5, 0] }}
                  autoAdjustOverflow={false}
                  getPopupContainer={(trigger) => trigger.parentElement!}
                  color="#A6C84D"
                  overlayInnerStyle={{
                    color: "rgba(0, 0, 0, 0.88)",
                    fontWeight: 500,
                  }}
                >
                  <div style={{ display: "inline-block", width: "100%" }}>
                    <Form.Item
                      name={[
                        "lineItems",
                        `${record?.serviceProviderId}`,
                        "products",
                        `${uuid + "-" + productId}`,
                        "tarifItems",
                        `${selectedPriceFamily?.id}`,
                        "priceFamilyValue",
                      ]}
                      rules={[{ required: true }]}
                      initialValue={
                        selectedPriceFamily?.priceValue &&
                          selectedPriceFamily.priceValue !== ""
                          ? selectedPriceFamily.priceValue
                          : 0
                      }
                      noStyle
                    >
                      <InputNumber
                        className="input-prix-unitaire price-option-selection"
                        suffix="€"
                        formatter={(value) =>
                          value!
                            .toString()
                            .replace(/\s/g, "")
                            .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")
                            .replace(".", ",")
                        }
                        parser={(value: string | undefined) =>
                          value!.replace(/\s/g, "").replace(/,/g, ".")
                        }
                        onChange={() => {
                          Promise.resolve().then(() => {
                            handlePriceValueSync(selectedPriceFamily?.id);
                          });
                        }}
                      />
                    </Form.Item>
                  </div>
                </Tooltip>
              </Form.Item>
              <PriceFamilyHiddenFields
                record={record}
                uuid={uuid}
                productId={productId}
                priceFamilyId={selectedPriceFamily?.id}
                selectedPriceFamily={selectedPriceFamily}
              />
            </Col>
          </Row>
        )}
        {selectedPriceFamily?.prices?.map((tarif, indexTarif) => (
          <Fragment key={`${tarif?.id}-${indexTarif}`}>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} id="info-tarif">
                <Form.Item
                  colon={false}
                  className="info-tarif-cell"
                  labelCol={{ span: 16 }}
                  label={
                    tarif.name +
                    `${tarif?.PriceTypeLogic?.key === PRICE_TYPE_LOGICS.indicative_purpose ? " (Informatif)" : ""}`
                  }
                ></Form.Item>
              </Col>
            </Row>
            {tarif?.priceOptions?.map((priceOption, indexPriceOption) => (
              <Fragment key={`${priceOption?.id}-${indexPriceOption}`}>
                <Row gutter={[16, 0]}>
                  <Col xs={{ span: 24 }} className="price-option">
                    {priceOption.priceTypeId === selectionPriceType?.id ? (
                      <>
                        <Form.Item
                          label={priceOption.name}
                          labelCol={{ span: 17 }}
                          name={[
                            "lineItems",
                            `${record?.serviceProviderId}`,
                            "products",
                            `${uuid + "-" + productId}`,
                            "tarifItems",
                            "productLinePrices",
                            "priceOption",
                            `${priceOption?.id}`,
                            "priceSubOptionId",
                          ]}
                          rules={[{ required: true }]}
                        >
                          <Select
                            placeholder="Sélectionner"
                            className="select-price-sub-options long-label-item price-option-selection"
                            showSearch={false}
                            options={priceOption?.PriceSubOptions?.map(
                              (priceSubOption) => ({
                                value: priceSubOption.id,
                                label: priceSubOption.name,
                              })
                            )}
                            onChange={() => {
                              Promise.resolve().then(() => {
                                handlePriceValueSync();
                              });
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          className="hidden"
                          name={[
                            "lineItems",
                            `${record?.serviceProviderId}`,
                            "products",
                            `${uuid + "-" + productId}`,
                            "tarifItems",
                            "productLinePrices",
                            "priceOption",
                            `${priceOption?.id}`,
                            "priceSubOptionLabel",
                          ]}
                        ></Form.Item>
                      </>
                    ) : (
                      <Form.Item
                        label={priceOption.name}
                        labelCol={{ span: 17 }}
                        name={[
                          "lineItems",
                          `${record?.serviceProviderId}`,
                          "products",
                          `${uuid + "-" + productId}`,
                          "tarifItems",
                          "productLinePrices",
                          "priceOption",
                          `${priceOption?.id}`,
                          "priceOptionValue",
                        ]}
                        rules={[{ required: true }]}
                        initialValue={
                          priceOption.priceValue && priceOption.priceValue != ""
                            ? priceOption.priceValue
                            : 0
                        }
                      >
                        {priceOption?.priceTypeId === numberPriceType?.id ? (
                          <div
                            style={{ display: "inline-block", width: "100%" }}
                          >
                            <Form.Item
                              name={[
                                "lineItems",
                                `${record?.serviceProviderId}`,
                                "products",
                                `${uuid + "-" + productId}`,
                                "tarifItems",
                                "productLinePrices",
                                "priceOption",
                                `${priceOption?.id}`,
                                "priceOptionValue",
                              ]}
                              rules={[{ required: true }]}
                              initialValue={
                                priceOption.priceValue &&
                                  priceOption.priceValue != ""
                                  ? priceOption.priceValue
                                  : 0
                              }
                              noStyle
                            >
                              <InputNumber
                                className="input-prix-unitaire long-label-item price-option-selection"
                                suffix={priceOption.unit}
                                formatter={(value) =>
                                  value!
                                    .toString()
                                    .replace(/\s/g, "")
                                    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")
                                    .replace(".", ",")
                                }
                                parser={(value: string | undefined) =>
                                  value!.replace(/\s/g, "").replace(/,/g, ".")
                                }
                                onChange={() => {
                                  Promise.resolve().then(() => {
                                    handlePriceValueSync(
                                      selectedPriceFamily?.id,
                                      tarif?.id,
                                      priceOption?.id
                                    );
                                  });
                                }}
                              />
                            </Form.Item>
                          </div>
                        ) : (
                          <Input
                            className="input-text-prix-unitaire long-label-item price-option-selection"
                            suffix={priceOption.unit}
                            onChange={() => {
                              Promise.resolve().then(() => {
                                handlePriceValueSync();
                              });
                            }}
                          />
                        )}
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </Fragment>
            ))}
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} className="price-option">
                <Form.Item
                  label={tarif.labelName}
                  labelCol={{ span: 17 }}
                  className="long-label-item-total-price"
                >
                  <Tooltip
                    title={(() => {
                      const priceValue = tarif?.priceValue;
                      if (!priceValue || Number(priceValue) === 0) {
                        return "Pas de prix";
                      }
                      const isManual =
                        tarif?.isManualPrice ||
                        tarif?.priceOptions?.some((opt) => opt.isManualPrice);
                      return isManual ? "Prix manuel" : "Prix catalogue";
                    })()}
                    placement="right"
                    align={{ offset: [5, 0] }}
                    autoAdjustOverflow={false}
                    getPopupContainer={(trigger) => trigger.parentElement!}
                    color="#A6C84D"
                    overlayInnerStyle={{
                      color: "rgba(0, 0, 0, 0.88)",
                      fontWeight: 500,
                    }}
                  >
                    <div style={{ display: "inline-block", width: "100%" }}>
                      <Form.Item
                        name={[
                          "lineItems",
                          `${record?.serviceProviderId}`,
                          "products",
                          `${uuid + "-" + productId}`,
                          "tarifItems",
                          "productLinePrices",
                          `${tarif?.id}`,
                          "priceValue",
                        ]}
                        rules={[{ required: true }]}
                        initialValue={
                          tarif.priceValue && tarif.priceValue != ""
                            ? tarif.priceValue
                            : 0
                        }
                        noStyle
                      >
                        <InputNumber
                          className="input-prix-unitaire long-label-item price-option-selection"
                          suffix="€"
                          formatter={(value) =>
                            value!
                              .toString()
                              .replace(/\s/g, "")
                              .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ")
                              .replace(".", ",")
                          }
                          parser={(value: string | undefined) =>
                            value!.replace(/\s/g, "").replace(/,/g, ".")
                          }
                          onChange={() => {
                            Promise.resolve().then(() => {
                              handlePriceValueSync(
                                selectedPriceFamily?.id,
                                tarif?.id
                              );
                            });
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Tooltip>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              className="hidden"
              name={[
                "lineItems",
                `${record?.serviceProviderId}`,
                "products",
                `${uuid + "-" + productId}`,
                "tarifItems",
                "productLinePrices",
                `${tarif?.id}`,
                "priceId",
              ]}
              initialValue={tarif?.id}
            ></Form.Item>
          </Fragment>
        ))}
      </>
    );
  } else {
    content = (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} className="text-align-right price-option">
            <Form.Item></Form.Item>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <section className="section mt-2 mb-2 generate-tarif-section">
      {content}
    </section>
  );
};

const PriceFamilyHiddenFields = ({
  record,
  uuid,
  productId,
  priceFamilyId,
  selectedPriceFamily,
}: {
  record: OrderPrestataireLine;
  uuid?: string;
  productId?: number;
  priceFamilyId: number;
  selectedPriceFamily?: PriceFamily;
}) => {
  return (
    <>
      <Form.Item
        className="hidden"
        name={[
          "lineItems",
          `${record?.serviceProviderId}`,
          "products",
          `${uuid + "-" + productId}`,
          "tarifItems",
          `${priceFamilyId}`,
          "linePriceFamilyId",
        ]}
      ></Form.Item>
      <Form.Item
        className="hidden"
        name={[
          "lineItems",
          `${record?.serviceProviderId}`,
          "products",
          `${uuid + "-" + productId}`,
          "tarifItems",
          `${priceFamilyId}`,
          "priceFamilyLabel",
        ]}
        initialValue={selectedPriceFamily?.labelName}
      ></Form.Item>
    </>
  );
};

export default CatalogPriceLineProductsCell;
